import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useDispatch } from 'react-redux';
import {
  addAdditionalTitleBarComponents,
  removeTitleBarComponent,
  toggleTitleBarLoading,
} from '../../../../redux/pages/actions';
import { Select } from '../../../../types/redux/pages/PagesStore';
import {
  APPEND_NEW_SELECT_TO_TITLEBAR,
  REMOVE_SELECT_FROM_TITLEBAR,
} from '../../../../types/redux/pages/pageTypes';
import useTitleBarSelect from '../../../../hooks/useTitleBarSelect';
import DisplayAreaCenteredWrapper from '../../../layout/utilities/displayAreaWrapper';
import MancoCounterpartySummaryTable from './MancoCounterpartySummaryTable.component';
import MancoCounterpartyBaseCurrencyExposureChart from './MancoCounterpartyBaseCurrencyExposureChart.component';
import MancoExposureTable from './MancoExposureTable.component';
import { Grid, Typography } from '@mui/material';
import { useMancoCounterpartyDropdownData, useMancoCounterpartyExposureData } from './queries';
import { GeneralOverviewPageRenderedComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';

interface MancoCounterpartyExposureContainerProps {
  selectedCounterparty: string;
  selectedDate: string | undefined;
}

function MancoCounterpartyExposureContainer(
  props: MancoCounterpartyExposureContainerProps,
): ReactElement {
  const { selectedCounterparty, selectedDate } = props;

  const {
    data: exposureData,
    isLoading,
    isFetching,
    isError,
  } = useMancoCounterpartyExposureData(selectedCounterparty, selectedDate);

  return (
    <QueryBoundary
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      data={exposureData}
      loadingMessages={['Loading Counterparty Exposure Data...', 'This may take a few moments...']}
      errorMessage="No positions available for this date"
    >
      {(data) => (
        <>
          <MancoCounterpartySummaryTable
            data={data}
            isFetching={isFetching}
          />
          <MancoCounterpartyBaseCurrencyExposureChart
            data={data}
            isFetching={isFetching}
          />
          <MancoExposureTable
            data={data}
            isFetching={isFetching}
            isCounterpartyExposure
          />
        </>
      )}
    </QueryBoundary>
  );
}

const MancoCounterpartyExposure: React.FC<
  GeneralOverviewPageRenderedComponentProps
> = (props) => {
  const activeDate = props.positionDate;
  const [counterpartyOptions, setCounterpartyOptions] = useState<any[] | null>(
    null,
  );

  const {
    data: counterpartyDropdownData,
    isLoading,
    isFetching,
    isError,
  } = useMancoCounterpartyDropdownData();

  const dispatch = useDispatch();
  const selectedCounterparty = useTitleBarSelect('counterpartySelection');
  const dropdownInitializedRef = useRef(false);

  useEffect(() => {
    dispatch(toggleTitleBarLoading(isLoading || isFetching));
    if (counterpartyDropdownData && !isLoading && !isFetching) {
      const dropdownOptions: any[] = [];
      // Transform the object shape { "95860": "Barclays Bank", ... } to dropdown options
      for (const [key, value] of Object.entries(counterpartyDropdownData)) {
        dropdownOptions.push({
          label: value,
          value: key,
        });
      }
      setCounterpartyOptions(dropdownOptions);
    }
  }, [counterpartyDropdownData, isLoading, isFetching, dispatch]);

  useEffect(() => {
    if (counterpartyDropdownData && !isLoading && !isFetching && counterpartyOptions && counterpartyOptions.length > 0 && !dropdownInitializedRef.current) {
      // Don't set a default - require user selection
      const currentValue = selectedCounterparty || 'none_selected';
      
      const selectsToAdd: Select[] = [
        {
          titleBarTitle: 'Select Counterparty',
          titleBarKey: 'counterpartySelection',
          values: counterpartyOptions,
          displayOnOverviewPage: true,
          aValueIsAlwaysSelected: false, // Don't require a selection
          currentValue: currentValue,
        },
      ];
      dispatch(
        addAdditionalTitleBarComponents(
          selectsToAdd,
          APPEND_NEW_SELECT_TO_TITLEBAR,
        ),
      );
      dispatch(toggleTitleBarLoading(false));
      dropdownInitializedRef.current = true;
    }

    return () => {
      if (dropdownInitializedRef.current) {
        dispatch(
          removeTitleBarComponent('counterpartySelection', REMOVE_SELECT_FROM_TITLEBAR),
        );
        dropdownInitializedRef.current = false;
      }
    };
  }, [counterpartyDropdownData, counterpartyOptions, selectedCounterparty, isLoading, isFetching, dispatch]);
  
  return (
    <QueryBoundary
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      data={counterpartyDropdownData}
      loadingMessages={[
        'Loading Counterparties...',
        'This May Take Few Moments',
      ]}
      errorMessage="No positions available for this date"
    >
      {() => (
        <Grid container spacing={2} style={{ padding: 8 }}>
          {!selectedCounterparty || selectedCounterparty === 'none_selected' ? (
            <DisplayAreaCenteredWrapper>
              <Typography variant="h2">Please Select a Counterparty</Typography>
            </DisplayAreaCenteredWrapper>
          ) : (
            <MancoCounterpartyExposureContainer
              selectedCounterparty={selectedCounterparty}
              selectedDate={activeDate}
            />
          )}
        </Grid>
      )}
    </QueryBoundary>
  );
};

export default MancoCounterpartyExposure;
